"use client";
import React from 'react';

interface SwitchProps {
  id?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * Locked, but still reachable by keyboard, so a screen reader hears it and its
   * aria-describedby reason. Keeps its on colour, so a read-only list stays readable.
   */
  softDisabled?: boolean;
  className?: string;
  /** Names the switch for screen readers when no <label htmlFor> points at it. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const Switch: React.FC<SwitchProps> = ({
  id,
  checked,
  onCheckedChange,
  disabled = false,
  softDisabled = false,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      disabled={disabled}
      aria-disabled={softDisabled || undefined}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition duration-150 ease-linear ${
        softDisabled
          ? 'cursor-not-allowed opacity-50'
          : disabled
          ? 'cursor-not-allowed opacity-50 bg-gray-100 dark:bg-gray-800'
          : 'cursor-pointer'
      } ${
        checked && !disabled
          ? ''
          : !disabled 
          ? 'bg-gray-200 dark:bg-white/10'
          : ''
      } focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${className}`}
      style={checked && !disabled ? { backgroundColor: '#0FB86A' } : undefined}
      onClick={() => !disabled && !softDisabled && onCheckedChange(!checked)}
    >
      <span
        className={`${
          checked ? 'translate-x-5' : 'translate-x-0'
        } absolute left-0.5 top-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow-theme-sm duration-150 ease-linear`}
      />
    </button>
  );
};

export default Switch; 